import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { ArrowLeft, FileText, Send, Save } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminReview = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [submission, setSubmission] = useState(null);
  const [extractedData, setExtractedData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [extracting, setExtracting] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    hearing_date: "",
    hearing_time: "",
    case_number: "",
    location: "",
    zoom_link: "",
    zoom_id: "",
    zoom_passcode: "",
    attorney_name: "",
    attorney_email: "",
    pay_rate: ""
  });

  useEffect(() => {
    fetchSubmissionAndExtract();
  }, [id]);

  const fetchSubmissionAndExtract = async () => {
    try {
      // Fetch submission details
      const response = await axios.get(`${API}/admin/submission/${id}`, {
        withCredentials: true
      });
      setSubmission(response.data);
      
      // Automatically extract text using AI
      setExtracting(true);
      try {
        const extractResponse = await axios.post(
          `${API}/admin/extract/${id}`,
          {},
          { withCredentials: true }
        );
        
        setExtractedData(extractResponse.data);
        
        // Auto-populate form with extracted data
        setFormData(prev => ({
          ...prev,
          hearing_date: extractResponse.data.hearing_date || "",
          hearing_time: extractResponse.data.hearing_time || "",
          case_number: extractResponse.data.case_number || "",
          location: extractResponse.data.location || "",
          zoom_link: extractResponse.data.zoom_link || "",
          zoom_id: extractResponse.data.zoom_id || "",
          zoom_passcode: extractResponse.data.zoom_passcode || ""
        }));
        
        if (extractResponse.data.hearing_date && extractResponse.data.case_number) {
          toast.success("Hearing details extracted successfully!");
        } else {
          toast.info("Extraction complete. Please review and fill in any missing fields.");
        }
      } catch (extractError) {
        console.error("Extract error:", extractError);
        toast.warning("Could not auto-extract all fields. Please fill in manually.");
        setExtractedData({ raw_text: "Extraction failed" });
      } finally {
        setExtracting(false);
      }
      
    } catch (error) {
      console.error("Fetch error:", error);
      if (error.response?.status === 401) {
        toast.error("Please login to access admin area");
        navigate("/admin/login");
      } else {
        toast.error("Failed to fetch submission");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleReExtract = async () => {
    setExtracting(true);
    try {
      const response = await axios.post(
        `${API}/admin/extract/${id}`,
        {},
        { withCredentials: true }
      );
      
      setExtractedData(response.data);
      
      // Re-populate form with extracted data
      setFormData(prev => ({
        ...prev,
        hearing_date: response.data.hearing_date || "",
        hearing_time: response.data.hearing_time || "",
        case_number: response.data.case_number || "",
        location: response.data.location || "",
        zoom_link: response.data.zoom_link || "",
        zoom_id: response.data.zoom_id || "",
        zoom_passcode: response.data.zoom_passcode || ""
      }));
      
      toast.success("Fields re-extracted successfully!");
    } catch (error) {
      console.error("Extract error:", error);
      toast.error("Failed to re-extract text from file");
    } finally {
      setExtracting(false);
    }
  };

  const handleSaveAssignment = async (emailMode) => {
    // Validate required fields
    if (!formData.hearing_date || !formData.hearing_time || !formData.case_number) {
      toast.error("Please fill in hearing date, time, and case number");
      return;
    }

    setSaving(true);
    try {
      // Create ledger entry
      await axios.post(
        `${API}/admin/ledger`,
        {
          submission_id: id,
          client_matter: submission.client_name,
          case_number: formData.case_number,
          hearing_date: formData.hearing_date,
          hearing_time: formData.hearing_time,
          location: formData.location,
          attorney_name: formData.attorney_name,
          attorney_email: formData.attorney_email,
          pay_rate: formData.pay_rate,
          zoom_link: formData.zoom_link,
          zoom_id: formData.zoom_id,
          zoom_passcode: formData.zoom_passcode
        },
        { withCredentials: true }
      );

      // Prepare email details
      const adminEmail = "therecruitingpartner@gmail.com";
      let recipients = [adminEmail];
      
      if (emailMode === "both" && formData.attorney_email) {
        recipients.push(formData.attorney_email);
      }

      // Create email body
      const emailBody = `Hearing Assignment Confirmation

Client/Matter: ${submission.client_name}
Case Number: ${formData.case_number}
Hearing Date: ${formData.hearing_date}
Hearing Time: ${formData.hearing_time}
Location: ${formData.location || "N/A"}

${formData.zoom_link ? `Zoom Link: ${formData.zoom_link}` : ""}
${formData.zoom_id ? `Meeting ID: ${formData.zoom_id}` : ""}
${formData.zoom_passcode ? `Passcode: ${formData.zoom_passcode}` : ""}

${formData.attorney_name ? `Assigned Attorney: ${formData.attorney_name}` : ""}
${formData.pay_rate ? `Pay Rate: ${formData.pay_rate}` : ""}

Client Email: ${submission.client_email}
${submission.notes ? `Notes: ${submission.notes}` : ""}

---
Sent via CoverDocket`;

      // Create Gmail compose link (opens Gmail web interface)
      const subject = encodeURIComponent(`Hearing Assignment - ${formData.case_number}`);
      const body = encodeURIComponent(emailBody);
      const to = recipients.join(',');
      
      const gmailLink = `https://mail.google.com/mail/?view=cm&fs=1&to=${to}&su=${subject}&body=${body}`;
      
      // Open Gmail in new tab
      window.open(gmailLink, '_blank');
      
      toast.success(`Assignment saved! Opening Gmail...`);
      
      // Navigate back to submissions after a delay
      setTimeout(() => navigate("/admin/submissions"), 2000);
    } catch (error) {
      console.error("Save error:", error);
      toast.error("Failed to save assignment");
    } finally {
      setSaving(false);
    }
  };

  if (loading || extracting) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#F5F5F5] to-[#e8f4f8] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-pulse text-[#6497B1] text-xl mb-2">
            {extracting ? "Extracting hearing details with AI..." : "Loading submission..."}
          </div>
          <p className="text-gray-600 text-sm">This may take a few seconds</p>
        </div>
      </div>
    );
  }

  if (!submission) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#F5F5F5] to-[#e8f4f8] flex items-center justify-center">
        <Card className="glass-card">
          <CardContent className="pt-6">
            <p className="text-red-600">Submission not found</p>
            <Button onClick={() => navigate("/admin/submissions")} className="mt-4">
              Back to Submissions
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F5F5F5] to-[#e8f4f8]">
      {/* Header */}
      <div className="header-gradient text-white py-6 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <Button
            onClick={() => navigate("/admin/submissions")}
            variant="ghost"
            className="text-white hover:bg-white/10 mb-4"
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Submissions
          </Button>
          <div className="flex items-center gap-4">
            <img 
              src="/coverdocket-logo.png" 
              alt="CoverDocket Logo" 
              className="h-14 w-auto"
            />
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Review Submission</h1>
              <p className="mt-1 text-lg opacity-90">Extract details and assign attorney</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Submission Info */}
        <Card className="glass-card border-0 mb-6">
          <CardHeader>
            <CardTitle className="text-xl font-semibold text-[#0A1128]">Submission Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-gray-600">Submission ID</p>
                <p className="font-mono text-sm">{submission.id}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">Client Name</p>
                <p className="font-medium">{submission.client_name}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">Client Email</p>
                <p>{submission.client_email}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">File</p>
                <p className="text-sm">{submission.filename}</p>
              </div>
            </div>
            {submission.notes && (
              <div className="mt-4">
                <p className="text-sm font-medium text-gray-600">Notes</p>
                <p className="text-sm mt-1">{submission.notes}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Extracted Raw Text */}
        {extractedData && extractedData.raw_text && (
          <Card className="glass-card border-0 mb-6">
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle className="text-lg font-semibold text-[#0A1128]">Extracted Text</CardTitle>
                  <CardDescription>Raw text extracted from the uploaded file</CardDescription>
                </div>
                <Button
                  onClick={handleReExtract}
                  disabled={extracting}
                  variant="outline"
                  size="sm"
                  data-testid="re-extract-button"
                >
                  {extracting ? "Re-extracting..." : "Re-extract"}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <Textarea
                value={extractedData.raw_text}
                readOnly
                rows={6}
                className="font-mono text-sm bg-gray-50"
                data-testid="extracted-text"
              />
            </CardContent>
          </Card>
        )}

        {/* Review Form */}
        <Card className="glass-card border-0">
            <CardHeader>
              <CardTitle className="text-xl font-semibold text-[#0A1128]">Review & Edit Details</CardTitle>
              <CardDescription>Review extracted information and add attorney details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Hearing Details */}
              <div>
                <h3 className="text-lg font-semibold text-[#0A1128] mb-4">Hearing Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="case_number">Case Number *</Label>
                    <Input
                      id="case_number"
                      data-testid="case-number-input"
                      value={formData.case_number}
                      onChange={(e) => setFormData({ ...formData, case_number: e.target.value })}
                      placeholder="2024-CA-000123"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="hearing_date">Hearing Date *</Label>
                    <Input
                      id="hearing_date"
                      data-testid="hearing-date-input"
                      value={formData.hearing_date}
                      onChange={(e) => setFormData({ ...formData, hearing_date: e.target.value })}
                      placeholder="04/30/2025"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="hearing_time">Hearing Time *</Label>
                    <Input
                      id="hearing_time"
                      data-testid="hearing-time-input"
                      value={formData.hearing_time}
                      onChange={(e) => setFormData({ ...formData, hearing_time: e.target.value })}
                      placeholder="9:30 AM"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="location">Location</Label>
                    <Input
                      id="location"
                      data-testid="location-input"
                      value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      placeholder="111 Centre St., Rm 5"
                    />
                  </div>
                </div>
              </div>

              <Separator />

              {/* Zoom Details */}
              <div>
                <h3 className="text-lg font-semibold text-[#0A1128] mb-4">Zoom Information (if applicable)</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="zoom_link">Zoom Link</Label>
                    <Input
                      id="zoom_link"
                      data-testid="zoom-link-input"
                      value={formData.zoom_link}
                      onChange={(e) => setFormData({ ...formData, zoom_link: e.target.value })}
                      placeholder="https://zoom.us/j/..."
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="zoom_id">Meeting ID</Label>
                    <Input
                      id="zoom_id"
                      data-testid="zoom-id-input"
                      value={formData.zoom_id}
                      onChange={(e) => setFormData({ ...formData, zoom_id: e.target.value })}
                      placeholder="123 456 7890"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="zoom_passcode">Passcode</Label>
                    <Input
                      id="zoom_passcode"
                      data-testid="zoom-passcode-input"
                      value={formData.zoom_passcode}
                      onChange={(e) => setFormData({ ...formData, zoom_passcode: e.target.value })}
                      placeholder="abc123"
                    />
                  </div>
                </div>
              </div>

              <Separator />

              {/* Attorney Details */}
              <div>
                <h3 className="text-lg font-semibold text-[#0A1128] mb-4">Attorney Assignment</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="attorney_name">Attorney Name</Label>
                    <Input
                      id="attorney_name"
                      data-testid="attorney-name-input"
                      value={formData.attorney_name}
                      onChange={(e) => setFormData({ ...formData, attorney_name: e.target.value })}
                      placeholder="Jane Smith"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="attorney_email">Attorney Email</Label>
                    <Input
                      id="attorney_email"
                      data-testid="attorney-email-input"
                      type="email"
                      value={formData.attorney_email}
                      onChange={(e) => setFormData({ ...formData, attorney_email: e.target.value })}
                      placeholder="jane@lawfirm.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="pay_rate">Pay Rate</Label>
                    <Input
                      id="pay_rate"
                      data-testid="pay-rate-input"
                      value={formData.pay_rate}
                      onChange={(e) => setFormData({ ...formData, pay_rate: e.target.value })}
                      placeholder="$125 flat"
                    />
                  </div>
                </div>
              </div>

              <Separator />

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={() => handleSaveAssignment("admin")}
                  disabled={saving}
                  className="flex-1 h-12 btn-secondary"
                  data-testid="email-admin-button"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {saving ? "Saving..." : "Save & Email Admin"}
                </Button>
                <Button
                  onClick={() => handleSaveAssignment("both")}
                  disabled={saving || !formData.attorney_email}
                  className="flex-1 h-12 btn-primary"
                  data-testid="email-both-button"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {saving ? "Saving..." : "Save & Email Admin + Attorney"}
                </Button>
              </div>
              
              <p className="text-sm text-gray-600 text-center">
                * Required fields. Emails are mocked and logged to console.
              </p>
            </CardContent>
          </Card>
      </div>
    </div>
  );
};

export default AdminReview;